export const REQUIRED_RESULT_ALGORITHM_KEYS = [
  'ga',
  'culture',
  'frog',
  'sflaga',
  'pso',
  'gapso',
] as const;

export const OPTIONAL_RESULT_ALGORITHM_KEYS = ['sflaGa'] as const;

export const RESULT_ALGORITHM_KEYS = [
  'ga',
  'culture',
  'frog',
  'sflaGa',
  'sflaga',
  'pso',
  'gapso',
] as const;

export type ResultAlgorithmKey = typeof RESULT_ALGORITHM_KEYS[number];

type AlgorithmSelectionLike = {
  includeSflaGaBaseline?: boolean;
};

type AlgorithmResultsLike = Partial<Record<ResultAlgorithmKey, unknown>> & {
  configuration?: AlgorithmSelectionLike;
};

export const RESULT_ALGORITHM_NAMES: Record<ResultAlgorithmKey, string> = {
  ga: 'GA',
  culture: 'Cultural',
  frog: 'SFLA',
  sflaGa: 'SFLA-GA',
  sflaga: 'SFLAGA',
  pso: 'PSO',
  gapso: 'GAPSO',
};

export const RESULT_ALGORITHM_COLORS: Record<ResultAlgorithmKey, string> = {
  ga: '#1976d2',
  culture: '#9c27b0',
  frog: '#ed6c02',
  sflaGa: '#7cb342',
  sflaga: '#2e7d32',
  pso: '#d32f2f',
  gapso: '#00897b',
};

export function getResultAlgorithmKeys(includeSflaGaBaseline: boolean = true): ResultAlgorithmKey[] {
  if (includeSflaGaBaseline) {
    return [...RESULT_ALGORITHM_KEYS];
  }

  return RESULT_ALGORITHM_KEYS.filter((key) => key !== 'sflaGa');
}

export function resolveIncludeSflaGaBaseline(
  value?: AlgorithmSelectionLike | null,
  hasSflaGaResult: boolean = false
): boolean {
  if (typeof value?.includeSflaGaBaseline === 'boolean') {
    return value.includeSflaGaBaseline;
  }

  return hasSflaGaResult;
}

export function getResultAlgorithmKeysForResults(
  results?: AlgorithmResultsLike | null
): ResultAlgorithmKey[] {
  const includeSflaGaBaseline = resolveIncludeSflaGaBaseline(
    results?.configuration,
    Boolean(results?.sflaGa)
  );

  return getResultAlgorithmKeys(includeSflaGaBaseline);
}

export function getResultAlgorithmKeysForResultsList(
  resultsList: Array<AlgorithmResultsLike | null | undefined>
): ResultAlgorithmKey[] {
  const includeSflaGaBaseline = resultsList.some((results) =>
    getResultAlgorithmKeysForResults(results).includes('sflaGa')
  );

  return getResultAlgorithmKeys(includeSflaGaBaseline);
}

export function getPipelineAlgorithmNames(includeSflaGaBaseline: boolean = true): string[] {
  return getResultAlgorithmKeys(includeSflaGaBaseline).map(
    (key) => RESULT_ALGORITHM_NAMES[key]
  );
}

export function getPipelineAlgorithmNamesForResults(
  results?: AlgorithmResultsLike | null
): string[] {
  return getResultAlgorithmKeysForResults(results).map(
    (key) => RESULT_ALGORITHM_NAMES[key]
  );
}

export function getPipelineAlgorithmCount(includeSflaGaBaseline: boolean = true): number {
  return getResultAlgorithmKeys(includeSflaGaBaseline).length;
}

export function getPipelineAlgorithmCountForResults(
  results?: AlgorithmResultsLike | null
): number {
  return getResultAlgorithmKeysForResults(results).length;
}
